import { CONVERSATION_EVENT_TYPES } from '@/lib/conversation/conversation-events'
import type { MessageAgent, MessageChannel, WebWidgetConfig } from '../../types'
import { resolveOfflineMessage, resolveOfflineSendCause } from '../shared/offline-message'
import type { InboundStoreOnlyReason } from '../shared/resolve-inbound-agent'

// ---------------------------------------------------------------------------
// Message visibility
// ---------------------------------------------------------------------------

/**
 * Service rows the visitor must never see — they exist for the operator, and a
 * customer reading «AI reply was not delivered» about their own conversation is
 * the opposite of what they are for. Filtered out of both widget paths, the init
 * history and the poll.
 *
 * Derived from `CONVERSATION_EVENT_TYPES` rather than listed again: the two
 * types added after this constant was written reached the widget as empty
 * bubbles, and each one also fired a `new-messages` notification at the host
 * page.
 */
export const WIDGET_HIDDEN_MESSAGE_TYPES = CONVERSATION_EVENT_TYPES

/**
 * Hidden markers that mean "no reply is coming", so the typing indicator must
 * stop even though the visitor sees nothing arrive.
 *
 * `session_start` is not one: it is inserted 1ms before the inbound that opened
 * the session, so it arrives while the reply is still being generated. Neither
 * is `handover_notified`: a non-pausing escalation still answers the customer.
 */
export const WIDGET_REPLY_RESOLVING_TYPES = ['reply_skipped', 'send_failed'] as const

/** True when this marker settles the question the typing indicator is asking. */
export function resolvesWidgetReply(message: { message_type: string }): boolean {
  return (WIDGET_REPLY_RESOLVING_TYPES as readonly string[]).includes(message.message_type)
}

/**
 * Columns the four visitor-facing endpoints may select. `content_payload` is excluded: markers
 * are hidden by type on the client, so their payload would still reach an anonymous visitor.
 */
export const WIDGET_MESSAGE_COLUMNS =
  'id, direction, message_type, content_text, is_ai_generated, created_at'

/** True when the message is one the visitor must not see rendered. */
export function isHiddenWidgetMessage(message: { direction: string; message_type: string }): boolean {
  return (
    message.direction === 'system' &&
    (WIDGET_HIDDEN_MESSAGE_TYPES as readonly string[]).includes(message.message_type)
  )
}

// ---------------------------------------------------------------------------
// Polling cadence
// ---------------------------------------------------------------------------

/** Adaptive idle backoff for an open chat: fast after activity, 15s at rest. */
export const POLL_INTERVALS = [1000, 1000, 1000, 2000, 2000, 5000, 5000, 10000, 15000] as const

/** Poll cadence while the chat popup is closed — only the unread badge consumes it. */
export const CLOSED_POLL_INTERVAL_MS = 60_000

export function getNextPollInterval(idleTicks: number): number {
  return POLL_INTERVALS[Math.min(idleTicks, POLL_INTERVALS.length - 1)]
}

export interface PollState {
  hidden: boolean
  chatOpen: boolean
  waitingForReply: boolean
  idleTicks: number
}

/**
 * Delay before the next poll, `null` = don't schedule. waitingForReply outranks
 * closed: a visitor can send then close, and the 1s phase is short-lived.
 */
export function resolvePollDelay({ hidden, chatOpen, waitingForReply, idleTicks }: PollState): number | null {
  if (hidden) return null
  if (waitingForReply) return 1000
  if (!chatOpen) return CLOSED_POLL_INTERVAL_MS
  return getNextPollInterval(idleTicks)
}

// ---------------------------------------------------------------------------
// Font definitions — single source of truth
// ---------------------------------------------------------------------------

export interface WidgetFont {
  /** Font family name used as the config value (e.g. "Inter") */
  value: string
  /** Human-readable label for UI selectors */
  label: string
  /** CSS `font-family` stack */
  css: string
}

/**
 * Canonical list of fonts available in the web widget.
 * Used by widget-chat (runtime), widget-preview, and web-widget-settings.
 */
export const WIDGET_FONTS: readonly WidgetFont[] = [
  { value: 'System', label: 'System Default', css: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" },
  { value: 'Inter', label: 'Inter', css: "'Inter', sans-serif" },
  { value: 'Poppins', label: 'Poppins', css: "'Poppins', sans-serif" },
  { value: 'Nunito', label: 'Nunito Rounded', css: "'Nunito', sans-serif" },
  { value: 'Playfair Display', label: 'Playfair Display', css: "'Playfair Display', serif" },
  { value: 'Merriweather', label: 'Merriweather', css: "'Merriweather', serif" },
  { value: 'Space Grotesk', label: 'Space Grotesk', css: "'Space Grotesk', sans-serif" },
  { value: 'JetBrains Mono', label: 'JetBrains Mono', css: "'JetBrains Mono', monospace" },
  { value: 'Caveat', label: 'Caveat', css: "'Caveat', cursive" },
] as const

/** Look up the CSS font-family stack for a given font value. Falls back to System. */
export function getFontCss(fontFamily?: string): string {
  const entry = WIDGET_FONTS.find(f => f.value === (fontFamily ?? 'System'))
  return entry?.css ?? WIDGET_FONTS[0].css
}

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

/** Darken a hex color by subtracting `amount` from each RGB channel. */
export function darkenColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, (num >> 16) - amount)
  const g = Math.max(0, ((num >> 8) & 0x00ff) - amount)
  const b = Math.max(0, (num & 0x0000ff) - amount)
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`
}

// ---------------------------------------------------------------------------
// LLM prompt hint
// ---------------------------------------------------------------------------

/**
 * Output-format instruction appended to the message-agent system prompt on
 * Markdown-format channels (`isMarkdownChannel`, see
 * `lib/agents/messaging/channels/channel-text-format.ts`).
 *
 * Must stay in sync with the `CHAT_MD_SANITIZE_SCHEMA` allowlist in
 * `components/ui/markdown-message.tsx`. Anything the LLM emits outside this
 * subset will be silently stripped by the sanitizer.
 */
export const WIDGET_MARKDOWN_PROMPT_HINT = `OUTPUT FORMAT:
Your replies are rendered in a web chat widget that supports a limited Markdown subset. You may use:
- Links as [text](url) — only http, https, or mailto URLs
- **bold**, *italic*, ~~strikethrough~~
- Bullet lists (-) and numbered lists
- Inline \`code\` and fenced code blocks
- > Blockquotes

Separate paragraphs with a blank line (two newlines). A single newline inside a paragraph will be collapsed to a space.

Do NOT use headings (# H1 ... ###### H6 — use **bold** for emphasis instead), task lists (- [ ] item), tables, images, raw HTML, or any other Markdown features — they will be stripped.`

// ---------------------------------------------------------------------------
// Default config
// ---------------------------------------------------------------------------

/** Default widget appearance config. Single source of truth. */
export const WIDGET_DEFAULT_WELCOME_MESSAGE = 'Hi! How can we help you today?'

// Rides ONLY the uncached responses (init + messages send) — the CDN-cached config
// route would lag a toggle flip by up to 15 min. Null = no state leaks while the agent answers.
export function resolveWidgetOfflineState(
  channel: MessageChannel,
  agent: Pick<MessageAgent, 'status'>,
): { offline: true; offlineMessage: string } | null {
  const cause = resolveOfflineSendCause({
    channelAiOff: channel.ai_replies_enabled === false,
    storeOnlyReason: widgetStoreOnlyReason(agent),
  })
  if (cause === null) return null
  const { enabled, text } = resolveOfflineMessage(channel)
  if (!enabled) return null
  return { offline: true, offlineMessage: text }
}

/** Only a disabled agent stores without replying — the validator already refused `draft`. */
export function widgetStoreOnlyReason(
  agent: Pick<MessageAgent, 'status'>,
): InboundStoreOnlyReason | undefined {
  return agent.status === 'disabled' ? 'agent_disabled' : undefined
}

/** Launcher offsets (px) from the side and bottom; phones keep the launcher in the corner. */
export const WIDGET_BUBBLE_OFFSET = { min: 10, max: 200, default: 20 } as const

/** Launcher diameter (px); phones keep the default. */
export const WIDGET_BUBBLE_SIZE = { min: 48, max: 100, default: 60 } as const

/**
 * Phones in either orientation. The loader (`PHONE_QUERY` in `public/widget-loader.js`) opens the chat full screen
 * and hides the launcher there, so the chat shows its own close button under the same query.
 */
export const WIDGET_PHONE_MEDIA_QUERY = '(max-width: 440px), (max-height: 500px)'

export const WIDGET_DEFAULT_CONFIG: WebWidgetConfig = {
  primaryColor: '#4F46E5',
  position: 'bottom-right',
  showBranding: true,
  welcomeMessage: WIDGET_DEFAULT_WELCOME_MESSAGE,
  title: 'Chat with us',
  avatarUrl: '',
  borderRadius: 12,
  bubbleStyle: 'filled',
  headerStyle: 'solid',
  headerSubtitle: 'Usually replies in minutes',
  bubbleAnimation: 'bounce',
  bubbleAnimationDelay: 3,
  fontFamily: 'System',
  fontSize: 14,
  bubbleOffsetX: WIDGET_BUBBLE_OFFSET.default,
  bubbleOffsetY: WIDGET_BUBBLE_OFFSET.default,
  bubbleSize: WIDGET_BUBBLE_SIZE.default,
  brandingText: 'Powered by Versafy.ai',
  typingIndicatorDelay: 4,
  // No `offline_message` here — the settings form publishes this object wholesale,
  // which would freeze the read-time default into channel configs.
}

/** The footer shows our own "Powered by" text rather than a tenant's custom text. */
export function usesDefaultBrandingText(brandingText: string | undefined): boolean {
  return brandingText === undefined || brandingText === WIDGET_DEFAULT_CONFIG.brandingText
}
